import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Users, Building2, Link2 } from 'lucide-react';

const MapInfoCard = ({
  location,
  activeOffices = [],
  onClose,
  onViewDetails,
  className = ''
}) => {
  if (!location) return null;

  const isActive = location.status === 'active';
  const officeCount = activeOffices.length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={`
          absolute z-50 bg-white rounded-xl shadow-xl border border-gray-200
          w-80 overflow-hidden
          ${className}
        `}
        style={{ 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          maxWidth: 'calc(100% - 32px)'
        }}
      >
        {/* Header */}
        <div className={`
          px-4 py-3 border-b
          ${isActive 
            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-100' 
            : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-100'
          }
        `}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{location.flag}</span>
              <div>
                <h3 className="font-semibold text-gray-900 leading-tight">
                  {location.name}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`
                    inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                    ${isActive 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                    }
                  `}>
                    {isActive && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                      </span>
                    )}
                    {isActive ? 'Active' : 'Planned'}
                  </span>
                </div>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-white/80 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {isActive ? (
          <>
            <div className="p-4 space-y-3">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <div className="text-xs text-gray-500 mb-0.5">Type</div>
                  <div className="text-sm font-medium text-gray-800 capitalize flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-gray-400" />
                    {location.type}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <div className="text-xs text-gray-500 mb-0.5">Continent</div>
                  <div className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                    {location.continent}
                  </div>
                </div>
              </div>

              {/* Population */}
              {location.population && (
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Population:</span>
                  <span className="font-medium text-gray-800">{location.population}</span>
                </div>
              )}

              {/* Capital */}
              {location.capital && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Capital:</span>
                  <span className="font-medium text-gray-800">{location.capital}</span>
                </div>
              )}

              {/* Active Offices */}
              {officeCount > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Active Officers ({officeCount})
                  </div>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {activeOffices.slice(0, 3).map((office, index) => (
                      <div 
                        key={office.id || index}
                        className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                      >
                        <span className="text-sm text-gray-700">{office.officer || 'Sports Officer'}</span>
                        {office.contactEmail && (
                          <span className="text-xs text-gray-500 truncate max-w-[9rem]" title={office.contactEmail}>
                            {office.contactEmail}
                          </span>
                        )}
                      </div>
                    ))}
                    {officeCount > 3 && (
                      <div className="text-xs text-center text-gray-500">
                        +{officeCount - 3} more officers
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer for Active — no static officer referral IDs */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex gap-2">
              <button
                disabled
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 
                         bg-gray-300 text-gray-500 text-sm font-medium 
                         rounded-lg cursor-not-allowed"
              >
                Link With Officer
                <Link2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Coming Soon Content for Planned */}
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-1">Coming Soon</h4>
              <p className="text-sm text-gray-500 mb-4">
                We're expanding to {location.name}! Be the first to establish a sports office here.
              </p>
              <button
                disabled
                className="inline-flex items-center justify-center gap-2 px-4 py-2 
                         bg-gray-300 text-gray-500 text-sm font-medium 
                         rounded-lg cursor-not-allowed w-full"
              >
                Link With Officer
                <Link2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default MapInfoCard;
